import { useCallback, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ImagePlus, X, GripVertical, AlertTriangle, CheckCircle2, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { validateImageFiles } from "@/lib/image-utils";
import { uploadCarImage } from "@/lib/car-image-pipeline";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

type CarRef = {
  id: string;
  brand: string;
  model: string;
  year: number;
  vin_number: string | null;
  images: string[] | null;
};

type StagedFile = {
  id: string;
  file: File;
  previewUrl: string;
};

type Group = {
  vin: string;
  car: CarRef | null; // null = unmatched
  files: StagedFile[];
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cars: CarRef[];
}

/** Extract VIN from a filename. VIN = 17 alphanumeric chars (no I, O, Q). */
function extractVinFromName(filename: string): string | null {
  const base = filename.replace(/\.[^.]+$/, "").toUpperCase();
  // Strict 17-char VIN
  const strict = base.match(/[A-HJ-NPR-Z0-9]{17}/);
  if (strict) return strict[0];
  // Fallback: any alphanumeric run >= 8 chars (for partial VINs / license plates)
  return null;
}

/** Match a filename to a car by checking if any car's VIN appears in the name. */
function findCarForFile(filename: string, cars: CarRef[]): { car: CarRef; vin: string } | null {
  const upper = filename.toUpperCase();

  // 1. Try strict VIN match in filename
  const extracted = extractVinFromName(filename);
  if (extracted) {
    const car = cars.find((c) => c.vin_number?.toUpperCase() === extracted);
    if (car) return { car, vin: extracted };
  }

  // 2. Try substring match for any known VIN (handles short or partial)
  for (const car of cars) {
    const vin = car.vin_number?.toUpperCase();
    if (vin && vin.length >= 6 && upper.includes(vin)) {
      return { car, vin };
    }
  }

  return null;
}

export function BulkImageUploadDialog({ open, onOpenChange, cars }: Props) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const carsWithVin = useMemo(() => cars.filter((c) => c.vin_number && c.vin_number.trim().length >= 6), [cars]);

  const reset = useCallback(() => {
    groups.forEach((g) => g.files.forEach((f) => URL.revokeObjectURL(f.previewUrl)));
    setGroups([]);
    setProgress(null);
    setCollapsed(new Set());
  }, [groups]);

  const handleFiles = useCallback(
    (fileList: FileList | File[]) => {
      // Hard-stop anything that would later be rejected by Google/Meta feeds
      // (>16 MB or wrong MIME type) before it ever lands in a staged group.
      const { accepted, rejected } = validateImageFiles(fileList);
      if (rejected.length > 0) {
        const first = rejected[0].error.message;
        toast.error(
          rejected.length === 1 ? "Billede afvist" : `${rejected.length} billeder afvist`,
          { description: first + (rejected.length > 1 ? ` (+${rejected.length - 1} mere)` : "") },
        );
      }
      if (accepted.length === 0) {
        return;
      }
      const incoming = accepted;

      setGroups((prev) => {
        const map = new Map<string, Group>();
        // Seed from existing groups
        for (const g of prev) {
          map.set(g.vin || `__unmatched__${g.files[0]?.id ?? Math.random()}`, g);
        }
        // Bucket: matched go under VIN; unmatched share the "__unmatched__" bucket
        const unmatchedKey = "__unmatched__";
        let unmatched = prev.find((g) => g.car === null);

        for (const file of incoming) {
          const match = findCarForFile(file.name, carsWithVin);
          const staged: StagedFile = {
            id: crypto.randomUUID(),
            file,
            previewUrl: URL.createObjectURL(file),
          };
          if (match) {
            const key = match.vin;
            const existing = map.get(key);
            if (existing) {
              existing.files.push(staged);
            } else {
              map.set(key, { vin: match.vin, car: match.car, files: [staged] });
            }
          } else {
            if (!unmatched) {
              unmatched = { vin: "", car: null, files: [] };
              map.set(unmatchedKey, unmatched);
            }
            unmatched.files.push(staged);
          }
        }

        // Sort each group's files by filename so 01_, 02_… land in order
        const next = Array.from(map.values()).map((g) => ({
          ...g,
          files: [...g.files].sort((a, b) => a.file.name.localeCompare(b.file.name, "da", { numeric: true })),
        }));
        // Matched groups first, unmatched last
        next.sort((a, b) => {
          if (a.car && !b.car) return -1;
          if (!a.car && b.car) return 1;
          return (a.car?.brand ?? "").localeCompare(b.car?.brand ?? "");
        });
        return next;
      });
    },
    [carsWithVin],
  );

  const removeFile = useCallback((groupVin: string, fileId: string) => {
    setGroups((prev) =>
      prev
        .map((g) => {
          if ((g.vin || "__unmatched__") !== (groupVin || "__unmatched__")) return g;
          const file = g.files.find((f) => f.id === fileId);
          if (file) URL.revokeObjectURL(file.previewUrl);
          return { ...g, files: g.files.filter((f) => f.id !== fileId) };
        })
        .filter((g) => g.files.length > 0),
    );
  }, []);

  const reorderFile = useCallback((groupVin: string, fromIdx: number, toIdx: number) => {
    setGroups((prev) =>
      prev.map((g) => {
        if ((g.vin || "__unmatched__") !== (groupVin || "__unmatched__")) return g;
        const next = [...g.files];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        return { ...g, files: next };
      }),
    );
  }, []);

  const toggleCollapse = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleClose = (next: boolean) => {
    if (uploading) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const matchedGroups = groups.filter((g) => g.car);
  const unmatchedGroup = groups.find((g) => !g.car);
  const totalMatched = matchedGroups.reduce((acc, g) => acc + g.files.length, 0);
  const totalUnmatched = unmatchedGroup?.files.length ?? 0;

  const handleUpload = async () => {
    if (matchedGroups.length === 0) {
      toast.error("Ingen billeder matchet til biler");
      return;
    }

    // RLS requires uploads into a folder named after the uploader's user_id.
    const { data: authData } = await supabase.auth.getUser();
    const userId = authData.user?.id;
    if (!userId) {
      toast.error("Du skal være logget ind for at uploade billeder");
      return;
    }

    setUploading(true);
    const total = totalMatched;
    let done = 0;
    setProgress({ done, total });

    let okCars = 0;
    let failedFiles = 0;

    for (const group of matchedGroups) {
      if (!group.car) continue;
      const newUrls: string[] = [];
      const existingCount = group.car.images?.length ?? 0;

      for (let idx = 0; idx < group.files.length; idx++) {
        const staged = group.files[idx];
        try {
          const result = await uploadCarImage(staged.file, {
            carId: group.car.id,
            userId,
            // The very first image we upload to a brand-new car becomes
            // the primary feed image.
            markFirstAsPrimary: existingCount === 0 && idx === 0,
          });
          newUrls.push(result.canonicalUrl);
        } catch (err) {
          console.error(`Upload failed for ${staged.file.name}`, err);
          failedFiles++;
        } finally {
          done++;
          setProgress({ done, total });
        }
      }

      if (newUrls.length > 0) {
        const merged = [...(group.car.images ?? []), ...newUrls];
        const { error: updateError } = await supabase
          .from("cars")
          .update({ images: merged })
          .eq("id", group.car.id);
        if (updateError) {
          console.error(`Failed to update car ${group.car.id}`, updateError);
          failedFiles += newUrls.length;
        } else {
          okCars++;
        }
      }
    }

    setUploading(false);
    setProgress(null);

    if (failedFiles === 0) {
      toast.success(`Tilføjede billeder til ${okCars} ${okCars === 1 ? "bil" : "biler"}`);
    } else {
      toast.warning(`Færdig — ${okCars} biler opdateret, ${failedFiles} billeder fejlede`);
    }

    await queryClient.invalidateQueries({ queryKey: ["admin-cars"] });
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk-upload af billeder via VIN</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground">Sådan virker det</p>
            <p className="mt-1">
              Træk billeder hertil. Filer hvis navn indeholder en bils <strong>VIN-nummer</strong> (17 tegn) bliver
              automatisk tilknyttet den rigtige bil. Eksempel:{" "}
              <code className="rounded bg-background px-1">WBA12345678901234_front.jpg</code>
            </p>
            <p className="mt-1">
              Nye billeder <strong>tilføjes</strong> bagerst i bilens eksisterende billed-liste — eksisterende
              billeder slettes ikke. Træk billeder for at ændre rækkefølgen før upload.
            </p>
          </div>

          {carsWithVin.length === 0 && (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-50/50 p-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <p>
                Ingen biler i dit lager har et VIN-nummer registreret. Tilføj VIN på bilerne først, ellers kan
                billederne ikke matches.
              </p>
            </div>
          )}

          {/* Drop zone */}
          <div
            className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border p-8 transition-colors hover:border-accent hover:bg-accent/5"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.classList.add("border-accent", "bg-accent/5");
            }}
            onDragLeave={(e) => e.currentTarget.classList.remove("border-accent", "bg-accent/5")}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove("border-accent", "bg-accent/5");
              if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
            }}
          >
            <ImagePlus size={32} className="text-muted-foreground" />
            <p className="mt-2 text-sm font-medium text-foreground">Klik eller træk billeder hertil</p>
            <p className="mt-1 text-xs text-muted-foreground">JPG, PNG eller WebP — filnavn skal indeholde VIN</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) handleFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </div>

          {/* Summary */}
          {groups.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/10 px-2.5 py-1 text-emerald-700 dark:text-emerald-400">
                <CheckCircle2 size={14} /> {totalMatched} matchet ({matchedGroups.length}{" "}
                {matchedGroups.length === 1 ? "bil" : "biler"})
              </span>
              {totalUnmatched > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-500/10 px-2.5 py-1 text-amber-700 dark:text-amber-400">
                  <AlertTriangle size={14} /> {totalUnmatched} uden match
                </span>
              )}
              <button
                type="button"
                onClick={reset}
                disabled={uploading}
                className="ml-auto text-xs text-muted-foreground underline hover:text-foreground"
              >
                Ryd alle
              </button>
            </div>
          )}

          {/* Groups */}
          <div className="space-y-3">
            {matchedGroups.map((g) => {
              const key = g.vin;
              const isCollapsed = collapsed.has(key);
              return (
                <div key={key} className="rounded-lg border border-border">
                  <button
                    type="button"
                    onClick={() => toggleCollapse(key)}
                    className="flex w-full items-center gap-3 p-3 text-left hover:bg-muted/40"
                  >
                    {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-foreground">
                        {g.car!.brand} {g.car!.model} <span className="text-muted-foreground">({g.car!.year})</span>
                      </p>
                      <p className="truncate font-mono text-xs text-muted-foreground">VIN: {g.vin}</p>
                    </div>
                    <span className="rounded-full bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent">
                      {g.files.length} {g.files.length === 1 ? "billede" : "billeder"}
                    </span>
                    {(g.car!.images?.length ?? 0) > 0 && (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                        +{g.car!.images!.length} eksisterende
                      </span>
                    )}
                  </button>

                  {!isCollapsed && (
                    <div className="border-t border-border p-3">
                      <DraggableGrid
                        files={g.files}
                        onReorder={(from, to) => reorderFile(g.vin, from, to)}
                        onRemove={(fileId) => removeFile(g.vin, fileId)}
                        disabled={uploading}
                      />
                    </div>
                  )}
                </div>
              );
            })}

            {unmatchedGroup && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-50/30 dark:bg-amber-950/20">
                <button
                  type="button"
                  onClick={() => toggleCollapse("__unmatched__")}
                  className="flex w-full items-center gap-3 p-3 text-left hover:bg-amber-50/60 dark:hover:bg-amber-950/30"
                >
                  {collapsed.has("__unmatched__") ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                      Ingen match — {unmatchedGroup.files.length}{" "}
                      {unmatchedGroup.files.length === 1 ? "fil" : "filer"}
                    </p>
                    <p className="text-xs text-amber-800/80 dark:text-amber-300/80">
                      Filnavnet indeholder ikke et kendt VIN-nummer. Disse uploades ikke.
                    </p>
                  </div>
                </button>
                {!collapsed.has("__unmatched__") && (
                  <div className="border-t border-amber-500/30 p-3">
                    <ul className="space-y-1 text-xs text-amber-900 dark:text-amber-200">
                      {unmatchedGroup.files.map((f) => (
                        <li key={f.id} className="flex items-center justify-between gap-2 font-mono">
                          <span className="truncate">{f.file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeFile("", f.id)}
                            className="shrink-0 text-amber-700 hover:text-amber-900 dark:text-amber-400"
                            aria-label="Fjern"
                          >
                            <X size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Progress */}
          {progress && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Uploader billeder...</span>
                <span className="tabular-nums">
                  {progress.done} / {progress.total}
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-accent transition-all"
                  style={{ width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 border-t border-border pt-4">
            <Button variant="outline" onClick={() => handleClose(false)} disabled={uploading}>
              Annuller
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || totalMatched === 0}
              className="bg-accent text-accent-foreground hover:opacity-90"
            >
              {uploading ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" /> Uploader...
                </>
              ) : (
                <>Upload {totalMatched > 0 ? `${totalMatched} billeder` : "billeder"}</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface GridProps {
  files: StagedFile[];
  onReorder: (from: number, to: number) => void;
  onRemove: (fileId: string) => void;
  disabled?: boolean;
}

function DraggableGrid({ files, onReorder, onRemove, disabled }: GridProps) {
  const dragIdx = useRef<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 md:grid-cols-5">
      {files.map((f, i) => (
        <div
          key={f.id}
          draggable={!disabled}
          onDragStart={() => (dragIdx.current = i)}
          onDragOver={(e) => {
            e.preventDefault();
            setOverIdx(i);
          }}
          onDragLeave={() => setOverIdx((cur) => (cur === i ? null : cur))}
          onDrop={(e) => {
            e.preventDefault();
            if (dragIdx.current !== null && dragIdx.current !== i) onReorder(dragIdx.current, i);
            dragIdx.current = null;
            setOverIdx(null);
          }}
          onDragEnd={() => {
            dragIdx.current = null;
            setOverIdx(null);
          }}
          className={`group relative aspect-[4/3] overflow-hidden rounded-md border bg-muted ${
            overIdx === i ? "border-accent ring-2 ring-accent/40" : "border-border"
          }`}
        >
          <img src={f.previewUrl} alt={f.file.name} loading="lazy" decoding="async" className="h-full w-full object-cover" />
          <div className="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
            #{i + 1}
          </div>
          <div className="absolute inset-0 flex items-start justify-end bg-black/0 p-1 opacity-0 transition-all group-hover:bg-black/40 group-hover:opacity-100">
            <button
              type="button"
              onClick={() => onRemove(f.id)}
              disabled={disabled}
              className="rounded-full bg-destructive p-1 text-destructive-foreground hover:opacity-80"
              aria-label="Fjern"
            >
              <X size={12} />
            </button>
          </div>
          <div className="pointer-events-none absolute bottom-1 left-1 flex items-center gap-1 rounded bg-black/60 px-1 py-0.5 text-[9px] text-white opacity-0 group-hover:opacity-100">
            <GripVertical size={10} /> Træk
          </div>
        </div>
      ))}
    </div>
  );
}
